import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Search, Calendar, MapPin, Users, Edit, Trash2, Eye } from 'lucide-react';
import { Card, Button, Input } from '@caruso/ui';
import { eventsService, EventStatus } from '@/services/events.service';

const STATUS_COLORS: Record<EventStatus, string> = {
  draft: 'bg-gray-500/20 text-gray-300',
  published: 'bg-blue-500/20 text-blue-300',
  completed: 'bg-green-500/20 text-green-300',
  cancelled: 'bg-red-500/20 text-red-300',
};

const STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

export function EventsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['events', search, statusFilter, page],
    queryFn: () => eventsService.getAll({
      search: search || undefined,
      status: statusFilter || undefined,
      page,
      limit: 10,
    }),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Eventos</h1>
          <p className="text-brand-ivory/80 mt-1 sm:mt-2 text-sm sm:text-base lg:text-lg">Gerencie seus eventos e convidados</p>
        </div>
        <Button onClick={() => navigate('/app/eventos/novo')} className="gap-2 w-full sm:w-auto">
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          Novo Evento
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-6 bg-brand-ink/50 border-brand-gold/20">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-brand-gold/50" />
              <Input
                placeholder="Buscar eventos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 sm:pl-11 placeholder:text-brand-ivory/40"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as EventStatus | '')}
            className="px-4 py-2.5 min-h-[44px] bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
            style={{
              colorScheme: 'dark',
              color: 'rgb(244, 241, 233) !important' as any,
              WebkitTextFillColor: 'rgb(244, 241, 233)',
            }}
          >
            <option value="">Todos os status</option>
            <option value="draft">Rascunho</option>
            <option value="published">Publicado</option>
            <option value="completed">Concluído</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
      </Card>

      {/* Events List */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6 bg-brand-ink/50 border-brand-gold/20 animate-pulse">
              <div className="h-20 bg-brand-smoke/50 rounded"></div>
            </Card>
          ))}
        </div>
      ) : data && data.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {data.data.map((event) => (
            <Card key={event.id} className="p-4 sm:p-6 bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4" onClick={() => navigate(`/app/eventos/${event.id}/editar`)}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-brand-gold font-serif">{event.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[event.status]}`}>
                      {STATUS_LABELS[event.status]}
                    </span>
                  </div>

                  {event.description && (
                    <p className="text-brand-ivory/70 mb-4 line-clamp-2">{event.description}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <Calendar className="w-4 h-4 text-brand-gold" />
                      <span>{format(new Date(event.eventDate), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-brand-ivory/70">
                        <MapPin className="w-4 h-4 text-brand-gold" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <Users className="w-4 h-4 text-brand-gold" />
                      <span>{event._count?.guests || 0} convidados</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/app/eventos/${event.id}/convidados`)}
                    title="Ver convidados"
                  >
                    <Eye className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/app/eventos/${event.id}/editar`)}
                    title="Editar"
                  >
                    <Edit className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 bg-brand-ink/50 border-brand-gold/20 text-center">
          <Calendar className="w-16 h-16 text-brand-gold/50 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-brand-gold mb-2">Nenhum evento encontrado</h3>
          <p className="text-brand-ivory/60 mb-6">Comece criando seu primeiro evento</p>
          <Button onClick={() => navigate('/app/eventos/novo')}>
            <Plus className="w-5 h-5 mr-2" />
            Criar Primeiro Evento
          </Button>
        </Card>
      )}

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(data.meta.totalPages)].map((_, i) => (
            <Button
              key={i}
              variant={page === i + 1 ? 'default' : 'outline'}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
